import { useState } from 'react'

import { Checkbox } from '@/components/ui'
import { StepDto, narrowStepTarget } from '@/common/models/gamification'
import {
  addStepDependency,
  removeStepDependency,
} from '@/core/services/gamification'

import { translationTitle } from '../shared/labels'

interface StepDependenciesFieldProps {
  step: StepDto
  allSteps: StepDto[]
  onChanged: () => Promise<void> | void
  onError: (e: unknown) => void
}

/** A short human label for a step, based on its resolved target. */
const stepLabel = (step: StepDto): string => {
  const narrowed = narrowStepTarget(step)
  const prefix = `Step ${step.ordinal + 1}`
  if (narrowed.targetType && narrowed.target && 'translations' in narrowed.target) {
    return `${prefix} · ${translationTitle(
      (narrowed.target as { translations?: { locale: string; title?: string }[] })
        .translations,
      narrowed.targetType,
    )}`
  }
  return prefix
}

/**
 * Toggles prerequisite edges for a step. Candidates are steps that come earlier
 * (lower ordinal) to avoid cycles. Each toggle immediately calls the dependency
 * add/remove endpoints, then refetches.
 */
export const StepDependenciesField = ({
  step,
  allSteps,
  onChanged,
  onError,
}: StepDependenciesFieldProps) => {
  const [busy, setBusy] = useState(false)

  const candidates = allSteps
    .filter((s) => s.id !== step.id && s.ordinal < step.ordinal)
    .sort((a, b) => a.ordinal - b.ordinal)

  const toggle = async (dependsOnStepId: string, checked: boolean) => {
    try {
      setBusy(true)
      if (checked) {
        await addStepDependency(step.id, { dependsOnStepId })
      } else {
        await removeStepDependency(step.id, dependsOnStepId)
      }
      await onChanged()
    } catch (e) {
      onError(e)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="border-t border-monochrome-120 pt-6">
      <div className="text-lg font-bold text-monochrome-1 mb-1">
        Dependencies
      </div>
      <div className="text-xs text-monochrome-2 mb-3">
        Steps that must be completed before this one unlocks.
        {step.unlockMode !== 'DEPENDENT' && (
          <span className="text-accent-3">
            {' '}
            Set unlock mode to DEPENDENT for these to take effect.
          </span>
        )}
      </div>
      {candidates.length === 0 ? (
        <div className="text-xs text-monochrome-2">
          No earlier steps to depend on.
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {candidates.map((candidate) => (
            <label
              key={candidate.id}
              className="flex items-center gap-3 cursor-pointer"
            >
              <Checkbox
                disabled={busy}
                checked={step.dependencies?.includes(candidate.id) ?? false}
                onCheckedChange={(checked) => toggle(candidate.id, !!checked)}
              />
              <span className="text-body-medium text-monochrome-1">
                {stepLabel(candidate)}
              </span>
            </label>
          ))}
        </div>
      )}
    </div>
  )
}
